import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Paper, Typography, List, ListItem, ListItemText, TextField, Button, Card, CardContent, CardMedia, Input, ListItemIcon, FormControl, Select, MenuItem, InputAdornment, IconButton, Tooltip, InputLabel, Tabs, Tab, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationSelector from '../components/LocationSelector';
import { 
  Public, 
  CalendarMonth, 
  Healing, 
  Restaurant, 
  LocalCafe, 
  Brush, 
  Checkroom, 
  Agriculture, 
  LocationOn, 
  LocalGroceryStore, 
  Spa, 
  RiceBowl, 
  RestaurantMenu, 
  BakeryDining, 
  Icecream, 
  EmojiNature, 
  Yard, 
  ChildCare, 
  Grass, 
  BugReport, 
  Build,
  Store,
  Search,
  Description,
  Close,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

// Styled components
const Sidebar = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(2),
  maxWidth: '260px',
  width: '100%',
}));

const CategoryList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const StoreList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE and Edge
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari and Opera
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(45deg, #5e35b1 30%, #1e88e5 90%)',
  color: 'white',
  padding: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
}));

const MainContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  width: '100%',
  margin: '0 auto',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StyledBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: '4px 8px',
  borderRadius: 12,
  fontSize: '0.75rem',
  fontWeight: 'bold',
}));

const SearchBarHeader = styled(Paper)(({ theme }) => ({
  position: 'relative',
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  gap: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[4],
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
}));

const VideoBackground = styled('video')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: -1,
});

const SearchOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'linear-gradient(45deg, rgba(94, 53, 177, 0.82) 30%, rgba(30, 136, 229, 0.72) 90%)',
  zIndex: -1,
}));

// Default logo component for stores
const DefaultStoreLogo = styled(Box)(({ theme }) => ({
  width: 20,
  height: 20,
  backgroundColor: '#e3f2fd',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #bbdefb',
}));

// Categories with hierarchical structure
const rajshahiDivisionProducts = [
  {
    category: { bangla: "আঞ্চলিক পণ্য", english: "Regional Products" },
    icon: <Public />,
    items: [
      { bangla: "রাজশাহী", english: "Rajshahi", icon: <LocationOn /> },
      { bangla: "নাটোর", english: "Natore", icon: <LocationOn /> },
      { bangla: "সিরাজগঞ্জ", english: "Sirajganj", icon: <LocationOn /> },
      { bangla: "পাবনা", english: "Pabna", icon: <LocationOn /> },
      { bangla: "বগুরা", english: "Bogra", icon: <LocationOn /> },
    ]
  },
  {
    category: { bangla: "মৌসুমি পণ্য", english: "Seasonal Products" },
    icon: <CalendarMonth />,
    items: [
      { bangla: "আম (রাজশাহী)", english: "Mango (Rajshahi)", icon: <LocalGroceryStore /> },
      { bangla: "খেজুরের গুর", english: "Date Palm Molasses", icon: <LocalGroceryStore /> },
      { bangla: "লিচু (রাজশাহী)", english: "Litchi (Rajshahi)", icon: <LocalGroceryStore /> },
      { bangla: "পেঁয়াজ (পাবনা)", english: "Onion (Pabna)", icon: <LocalGroceryStore /> },
    ]
  },
  {
    category: { bangla: "ওষুধি পণ্য", english: "Medicinal Products" },
    icon: <Healing />,
    items: [
      { bangla: "হলুদ", english: "Turmeric", icon: <Spa /> },
      { bangla: "তুলসী পাতা", english: "Basil Leaves", icon: <Spa /> },
      { bangla: "আদা", english: "Ginger", icon: <Spa /> },
      { bangla: "মেথি", english: "Fenugreek", icon: <Spa /> },
    ]
  },
  {
    category: { bangla: "খাদ্য পণ্য", english: "Food Products" },
    icon: <Restaurant />,
    items: [
      { bangla: "চাল", english: "Rice", icon: <RiceBowl /> },
      { bangla: "ডাল", english: "Lentils", icon: <RestaurantMenu /> },
      { bangla: "আটা", english: "Flour", icon: <BakeryDining /> },
      { bangla: "চিনি", english: "Sugar", icon: <Icecream /> },
    ]
  },
  {
    category: { bangla: "দুধ ও দুগ্ধজাত", english: "Milk & Dairy Products" },
    icon: <LocalCafe />,
    items: [
      { bangla: "গরুর দুধ", english: "Cow Milk", icon: <LocalCafe /> },
      { bangla: "দই (বগুড়া)", english: "Yogurt (Bogra)", icon: <Icecream /> },
      { bangla: "ঘি", english: "Ghee", icon: <BakeryDining /> },
    ]
  },
  {
    category: { bangla: "হস্তশিল্প", english: "Handicrafts" },
    icon: <Brush />,
    items: [
      { bangla: "নকশিকাঁথা", english: "Nakshi Kantha", icon: <Checkroom /> },
      { bangla: "মাটির পাত্র", english: "Clay Pots", icon: <EmojiNature /> },
      { bangla: "বাঁশের পণ্য", english: "Bamboo Products", icon: <Yard /> },
    ]
  },
  {
    category: { bangla: "তৈরি পোশাক", english: "Ready-Made Garments" },
    icon: <Checkroom />,
    items: [
      { bangla: "সালোয়ার কামিজ", english: "Salwar Kameez", icon: <Checkroom /> },
      { bangla: "শাড়ি", english: "Saree", icon: <Checkroom /> },
      { bangla: "পাঞ্জাবি", english: "Panjabi", icon: <Checkroom /> },
      { bangla: "শিশুদের পোশাক", english: "Children's Wear", icon: <ChildCare /> },
    ]
  },
  {
    category: { bangla: "কৃষি উপকরণ", english: "Agricultural Inputs" },
    icon: <Agriculture />,
    items: [
      { bangla: "বীজ", english: "Seeds", icon: <Spa /> },
      { bangla: "সার", english: "Fertilizer", icon: <Grass /> },
      { bangla: "কীটনাশক", english: "Pesticides", icon: <BugReport /> },
      { bangla: "চাষের যন্ত্রপাতি", english: "Farming Tools", icon: <Build /> },
    ]
  }
];

const stores = [
  {
    name: 'রাজশাহী পণ্য ভান্ডার',
    englishName: 'Rajshahi Product Store',
    logo: '/images/logos/rajshahi.png',
    description: 'রাজশাহী বিভাগের সেরা পণ্য'
  },
  {
    name: 'নাটোর ট্রেডিং',
    englishName: 'Natore Trading',
    logo: '/images/logos/natore.png',
    description: 'নাটোরের ঐতিহ্যবাহী পণ্য'
  },
  {
    name: 'পাবনা এগ্রো',
    englishName: 'Pabna Agro',
    logo: '/images/logos/pabna.png',
    description: 'পাবনার কৃষি পণ্য বিশেষজ্ঞ'
  },
  {
    name: 'বগুড়া স্পেশাল',
    englishName: 'Bogra Special',
    logo: '/images/logos/bogra.png',
    description: 'বগুড়ার বিখ্যাত দই ও পণ্য'
  },
  {
    name: 'সিরাজগঞ্জ মার্ট',
    englishName: 'Sirajganj Mart',
    logo: '/images/logos/sirajganj.png',
    description: 'সিরাজগঞ্জের স্থানীয় পণ্য'
  },
  {
    name: 'রাজশাহী ঐতিহ্য',
    englishName: 'Rajshahi Heritage',
    logo: '/images/logos/heritage.png',
    description: 'ঐতিহ্যবাহী হস্তশিল্প ও পণ্য'
  },
  {
    name: 'উত্তরাঞ্চল এক্সপ্রেস',
    englishName: 'Northern Express',
    logo: '/images/logos/northern.png',
    description: 'উত্তরাঞ্চলের সকল পণ্য'
  },
  {
    name: 'রাজশাহী অর্গানিক',
    englishName: 'Rajshahi Organic',
    logo: '/images/logos/organic.png',
    description: 'জৈব ও প্রাকৃতিক পণ্য'
  },
];

const products = [
  {
    id: 1,
    name: 'রাজশাহীর আম (ল্যাংড়া)',
    englishName: 'Rajshahi Mango (Langra)',
    category: 'আম (রাজশাহী)',
    store: 'রাজশাহী পণ্য ভান্ডার',
    price: '৳৮০.০০',
    image: '/images/products/mango.jpg',
    description: 'রাজশাহীর বিখ্যাত ল্যাংড়া আম। মিষ্টি ও সুস্বাদু, প্রাকৃতিকভাবে পাকানো।',
    origin: 'রাজশাহী'
  },
  {
    id: 2,
    name: 'খেজুরের গুড় (খাঁটি)',
    englishName: 'Pure Date Palm Molasses',
    category: 'খেজুরের গুর',
    store: 'নাটোর ট্রেডিং',
    price: '৳১২০.০০',
    image: '/images/products/gur.jpg',
    description: 'শীতকালে তাজা খেজুরের রস থেকে তৈরি খাঁটি গুড়। পুষ্টিকর ও স্বাস্থ্যকর।',
    origin: 'নাটোর'
  },
  {
    id: 3,
    name: 'পাবনার পেঁয়াজ',
    englishName: 'Pabna Onion',
    category: 'পেঁয়াজ (পাবনা)',
    store: 'পাবনা এগ্রো',
    price: '৳৪৫.০০',
    image: '/images/products/onion.jpg',
    description: 'পাবনায় উৎপাদিত উন্নত মানের পেঁয়াজ। তীব্র গন্ধ ও স্বাদযুক্ত।',
    origin: 'পাবনা'
  },
  {
    id: 4,
    name: 'বগুড়ার দই',
    englishName: 'Bogra Yogurt',
    category: 'দই (বগুড়া)',
    store: 'বগুড়া স্পেশাল',
    price: '৳৬০.০০',
    image: '/images/products/doi.jpg',
    description: 'বগুড়ার ঐতিহ্যবাহী মিষ্টি দই। খাঁটি গরুর দুধ থেকে তৈরি।',
    origin: 'বগুড়া'
  },
  {
    id: 5,
    name: 'হলুদ গুঁড়া (জৈব)',
    englishName: 'Organic Turmeric Powder',
    category: 'হলুদ',
    store: 'রাজশাহী অর্গানিক',
    price: '৳৭৫.০০',
    image: '/images/products/turmeric.jpg',
    description: 'রাজশাহীতে উৎপাদিত জৈব হলুদ। ঔষধি গুণাগুণ সমৃদ্ধ।',
    origin: 'রাজশাহী'
  },
  {
    id: 6,
    name: 'নকশিকাঁথা (হস্তনির্মিত)',
    englishName: 'Handmade Nakshi Kantha',
    category: 'নকশিকাঁথা',
    store: 'রাজশাহী ঐতিহ্য',
    price: '৳১৫০০.০০',
    image: '/images/products/kantha.jpg',
    description: 'হাতে তৈরি ঐতিহ্যবাহী নকশিকাঁথা। বাংলার ঐতিহ্যের নিদর্শন।',
    origin: 'রাজশাহী'
  },
  {
    id: 7,
    name: 'মিনিকেট চাল',
    englishName: 'Miniket Rice',
    category: 'চাল',
    store: 'সিরাজগঞ্জ মার্ট',
    price: '৫৫.০০',
    image: '/images/products/rice.jpg',
    description: 'উন্নত জাতের মিনিকেট চাল। সুগন্ধি ও পুষ্টিকর।',
    origin: 'সিরাজগঞ্জ'
  },
  {
    id: 8,
    name: 'তুলসী পাতা (শুকনো)',
    englishName: 'Dried Basil Leaves',
    category: 'তুলসী পাতা',
    store: 'রাজশাহী অর্গানিক',
    price: '৳৪০.০০',
    image: '/images/products/tulsi.jpg',
    description: 'প্রাকৃতিকভাবে শুকানো তুলসী পাতা। ঔষধি গুণে ভরপুর।',
    origin: 'রাজশাহী'
  },
  {
    id: 9,
    name: 'বাঁশের ঝুড়ি',
    englishName: 'Bamboo Basket',
    category: 'বাঁশের পণ্য',
    store: 'রাজশাহী ঐতিহ্য',
    price: '৳২৫০.০০',
    image: '/images/products/basket.jpg',
    description: 'হস্তনির্মিত বাঁশের ঝুড়ি। টেকসই ও পরিবেশবান্ধব।',
    origin: 'রাজশাহী'
  },
  {
    id: 10,
    name: 'খাঁটি ঘি',
    englishName: 'Pure Ghee',
    category: 'ঘি',
    store: 'বগুড়া স্পেশাল',
    price: '৩৮০.০০',
    image: '/images/products/ghee.jpg',
    description: 'গরুর দুধ থেকে তৈরি খাঁটি ঘি। সুগন্ধ ও স্বাদে অতুলনীয়।',
    origin: 'বগুড়া'
  },
  {
    id: 11,
    name: 'জৈব সার',
    englishName: 'Organic Fertilizer',
    category: 'সার',
    store: 'পাবনা এগ্রো',
    price: '৩৫.০০',
    image: '/images/products/fertilizer.jpg',
    description: 'প্রাকৃতিক উপাদান থেকে তৈরি জৈব সার। মাটি ও ফসলের জন্য নিরাপদ।',
    origin: 'পাবনা'
  },
  {
    id: 12,
    name: 'কুটির শিল্পের শাড়ি',
    englishName: 'Cottage Industry Saree',
    category: 'শাড়ি',
    store: 'রাজশাহী ঐতিহ্য',
    price: '৮৫০.০০',
    image: '/images/products/saree.jpg',
    description: 'হাতে বোনা ঐতিহ্যবাহী শাড়ি। বাংলার কারুশিল্পের নিদর্শন।',
    origin: 'রাজশাহী'
  }
];

const NationalBazar = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreTab, setSelectedStoreTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const { addToCart } = useCart();

  // Debounce search query to improve performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pre-computed category lookup for better performance
  const categoryLookup = useMemo(() => {
    const lookup = new Map();
    rajshahiDivisionProducts.forEach(categoryData => {
      categoryData.items.forEach(item => {
        lookup.set(item.bangla, categoryData.category.bangla);
      });
    });
    return lookup;
  }, []);

  const handleCategoryClick = useCallback((categoryKey) => {
    // Toggle expanded state for this category
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  }, []);

  const handleSubCategoryClick = useCallback((mainCategoryKey, subCategory) => {
    setSelectedCategory(mainCategoryKey);
    setSelectedSubCategory(subCategory);
  }, []);

  const handleStoreClick = useCallback((store) => {
    setSelectedStore(store);
    setSelectedStoreTab(0); // Reset tab to "All" when selecting a new store
  }, []);

  // Get unique categories for the selected store
  const getStoreCategoriesWithCounts = useMemo(() => {
    if (!selectedStore) return [];
    
    const storeProducts = products.filter(product => product.store === selectedStore.name);
    const categoryMap = new Map();
    
    // Count products in each category
    storeProducts.forEach(product => {
      const count = categoryMap.get(product.category) || 0;
      categoryMap.set(product.category, count + 1);
    });
    
    // Convert to array and add "All" option
    const categories = [
      { name: 'সকল পণ্য', count: storeProducts.length },
      ...Array.from(categoryMap.entries()).map(([name, count]) => ({ name, count }))
    ];
    
    return categories;
  }, [selectedStore]);

  const handleStoreTabChange = useCallback((event, newValue) => {
    setSelectedStoreTab(newValue);
  }, []);

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Simplified category matching
      let matchesCategory = true;
      if (selectedCategory) {
        if (selectedSubCategory) {
          matchesCategory = product.category === selectedSubCategory;
        } else {
          // Use pre-computed lookup for better performance
          matchesCategory = categoryLookup.get(product.category) === selectedCategory;
        }
      }
      
      const matchesStore = !selectedStore || product.store === selectedStore.name;
      
      // Simplified store tab filtering
      let matchesStoreTab = true;
      if (selectedStore && selectedStoreTab > 0 && getStoreCategoriesWithCounts[selectedStoreTab]) {
        matchesStoreTab = product.category === getStoreCategoriesWithCounts[selectedStoreTab].name;
      }
      
      // Simplified search matching
      const matchesSearch = !debouncedSearchQuery || 
        product.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
        product.englishName.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
        
      return matchesCategory && matchesStore && matchesStoreTab && matchesSearch;
    });
  }, [selectedCategory, selectedSubCategory, selectedStore, selectedStoreTab, debouncedSearchQuery, getStoreCategoriesWithCounts, categoryLookup]);

  const handleAddToCart = useCallback((product) => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      store: product.store,
      stock: product.stock || 10 // Default stock if not provided
    }, 1); // Add 1 quantity by default
  }, [addToCart]);

  return (
    <>
      <Header />
      <Box sx={{ flexGrow: 1, p: 2, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        {/* Search Header Bar */}
        <SearchBarHeader>
          <VideoBackground autoPlay muted loop>
            <source src="/videos/default.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
          <SearchOverlay />
          
          {/* Location Selector */}
          <LocationSelector 
            variant="search-bar"
            onLocationChange={(location) => {
              console.log('Location changed:', location);
            }}
          />

          {/* Animated Search Field */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="পণ্যের নাম, ব্র্যান্ড বা ক্যাটেগরি লিখুন..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              mx: { xs: 0, sm: 2 },
              mt: { xs: 1, sm: 0 },
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              flexGrow: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />
                </InputAdornment>
              ),
            }}
          />
        </SearchBarHeader>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: '260px' }, 
            minWidth: { md: '260px' },
            pr: { md: 2 }, 
            mb: { xs: 2, md: 0 } 
          }}>
            <Sidebar elevation={3}>
              <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <SectionHeader>
                  <Public sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    পণ্যের ধরন
                  </Typography>
                </SectionHeader>
                <CategoryList sx={{ pl: 1, pr: 1 }}>
                  {rajshahiDivisionProducts.map((categoryData, index) => {
                    const categoryKey = categoryData.category.bangla;
                    return (
                      <React.Fragment key={categoryKey}>
                        <ListItem
                          button
                          onClick={() => handleCategoryClick(categoryKey)}
                          selected={selectedCategory === categoryKey}
                          sx={{ py: 1 }}
                        >
                          <ListItemIcon>
                            {categoryData.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={categoryData.category.bangla}
                            secondary={categoryData.category.english}
                            primaryTypographyProps={{ 
                              style: { 
                                fontWeight: selectedCategory === categoryKey ? 'bold' : 'normal',
                                fontSize: '0.9rem'
                              } 
                            }}
                            secondaryTypographyProps={{ 
                              style: { 
                                fontSize: '0.75rem',
                                color: 'text.secondary'
                              } 
                            }}
                          />
                          {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {categoryData.items.map((item) => (
                              <ListItem
                                button
                                key={`${categoryKey}-${item.bangla}`}
                                sx={{ pl: 4, py: 0.5 }}
                                selected={selectedCategory === categoryKey && selectedSubCategory === item.bangla}
                                onClick={() => handleSubCategoryClick(categoryKey, item.bangla)}
                              >
                                <ListItemIcon>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={item.bangla}
                                  secondary={item.english}
                                  primaryTypographyProps={{ 
                                    style: { 
                                      fontSize: '0.8rem',
                                      fontWeight: selectedCategory === categoryKey && selectedSubCategory === item.bangla ? 'bold' : 'normal'
                                    } 
                                  }}
                                  secondaryTypographyProps={{ 
                                    style: { fontSize: '0.7rem' } 
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    );
                  })}
                </CategoryList>

                <SectionHeader sx={{ mt: 2 }}>
                  <Store sx={{ mr: 1 }} />
                  <Typography variant="h6" component="div">
                    দোকানের তালিকা
                  </Typography>
                </SectionHeader>
                <StoreList sx={{ pl: 1, pr: 1 }}>
                  {stores.map((store) => (
                    <ListItem
                      button
                      key={store.name}
                      selected={selectedStore?.name === store.name}
                      onClick={() => handleStoreClick(store)}
                      sx={{ py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <DefaultStoreLogo>
                          <Store sx={{ fontSize: 12, color: '#1976d2' }} />
                        </DefaultStoreLogo>
                      </ListItemIcon>
                      <ListItemText 
                        primary={store.name}
                        secondary={`${store.englishName} • ${store.description}`}
                        primaryTypographyProps={{ 
                          fontSize: '0.875rem',
                          fontWeight: selectedStore?.name === store.name ? 'bold' : 'normal' 
                        }}
                        secondaryTypographyProps={{ 
                          fontSize: '0.75rem',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItem>
                  ))}
                </StoreList>
              </Box>
            </Sidebar>
          </Box>

          {/* Main Content */}
          <Box sx={{ 
            width: { xs: '100%', md: 'calc(100% - 260px)' },
            flexGrow: 1 
          }}>
            <MainContent>
              {/* Store Details Section */}
              {selectedStore && (
                <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(45deg, #f8f9fa 30%, #e3f2fd 90%)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ 
                      width: 60, 
                      height: 60, 
                      backgroundColor: '#e3f2fd',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #bbdefb',
                      mr: 3
                    }}>
                      <Store sx={{ fontSize: 30, color: '#1976d2' }} />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                        {selectedStore.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                        {selectedStore.englishName}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        {selectedStore.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            সেবা এলাকা: রাজশাহী বিভাগ
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Store sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" color="text.secondary">
                            পণ্যের সংখ্যা: {filteredProducts.length}টি
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedStore(null)}
                      startIcon={<Close />}
                      sx={{ ml: 2 }}
                    >
                      বন্ধ করুন
                    </Button>
                  </Box>
                  <Typography variant="h6" sx={{ color: '#1976d2', mb: 1 }}>
                    {selectedStore.name} এর পণ্যসমূহ
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    নিচে {selectedStore.name} দোকানের সকল উপলব্ধ পণ্য দেখানো হয়েছে। আপনি চাইলে ক্যাটেগরি অনুযায়ী ফিল্টার করতে পারেন।
                  </Typography>
                  
                  {/* Category Tabs */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs 
                      value={selectedStoreTab} 
                      onChange={handleStoreTabChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          minWidth: 'auto',
                          px: 2,
                          py: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#1976d2',
                        }
                      }}
                    >
                      {getStoreCategoriesWithCounts.map((category, index) => (
                        <Tab 
                          key={index}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
                                {category.name}
                              </Typography>
                              <Box sx={{ 
                                backgroundColor: selectedStoreTab === index ? '#1976d2' : '#e0e0e0',
                                color: selectedStoreTab === index ? 'white' : '#666',
                                borderRadius: '12px',
                                px: 1,
                                py: 0.25,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                minWidth: '20px',
                                textAlign: 'center'
                              }}>
                                {category.count}
                              </Box>
                            </Box>
                          }
                          sx={{
                            color: selectedStoreTab === index ? '#1976d2' : 'text.secondary',
                            '&.Mui-selected': {
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }
                          }}
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Paper>
              )}
              
              {/* Product Grid */}
              {filteredProducts.length > 0 ? (
                <Box
                  sx={{
                    display: 'grid',
                    gap: 3,
                    gridTemplateColumns: {
                      xs: 'repeat(1, 1fr)', // Mobile portrait: 1 card per row
                      sm: 'repeat(2, 1fr)', // Mobile horizontal: 2 cards per row
                      md: 'repeat(5, 1fr)', // Laptop: 5 cards per row
                      lg: 'repeat(6, 1fr)', // Big screen: 6 cards per row
                      '@media (min-width: 1980px)': {
                        gridTemplateColumns: 'repeat(9, 1fr)' // Extra large screens: 9 cards per row
                      }
                    },
                    width: '100%',
                  }}
                >
                  {filteredProducts.map((product) => (
                    <Box key={product.id} sx={{ display: 'flex', width: '100%' }}>
                      <ProductCard sx={{ width: '100%' }}>
                        <Box sx={{ position: 'relative' }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={product.image}
                            alt={product.name}
                            onError={(e) => {
                              e.target.src = '/images/products/default.png';
                            }}
                          />
                          <StyledBadge>
                            {product.category}
                          </StyledBadge>
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography gutterBottom variant="h6" component="div">
                            {product.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {product.englishName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            উৎপাদনকারী: {product.origin}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {product.description}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" color="primary">
                              {product.price}
                            </Typography>
                            <Button 
                              variant="contained" 
                              size="small"
                              onClick={() => handleAddToCart(product)}
                            >
                              কার্টে যোগ করুন
                            </Button>
                          </Box>
                        </CardContent>
                      </ProductCard>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    কোন পণ্য পাওয়া যায়নি
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    অন্য ক্যাটেগরি বা দোকান নির্বাচন করুন
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 2 }}
                    onClick={() => {
                      setSelectedCategory('');
                      setSelectedSubCategory('');
                      setSelectedStore(null);
                      setSelectedStoreTab(0);
                      setSearchQuery('');
                    }}
                  >
                    সব ফিল্টার মুছুন
                  </Button>
                </Box>
              )}
            </MainContent>
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: "50px" }}>
        <Footer />
      </Box>
    </>
  );
};

export default NationalBazar;
